import { defineStore } from 'pinia';

export const useAudioPlayerStore = defineStore('audioPlayer', {
  state: () => ({
    isPlaying: false,
    currentCuento: null,
    audioUrl: null,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isVisible: true, // Siempre visible por defecto
    audioElement: null,
    isLoading: false, // Para evitar múltiples cargas
    prefetchedAudios: new Map(), // Cache para audios prefetcheados
    isPrefetching: false, // Estado de prefetch
    autoPlayDirection: 'next' // Dirección por defecto: siguiente cuento
  }),

  persist: {
    key: 'audio-player-state',
    storage: localStorage,
    paths: ['currentCuento', 'audioUrl', 'currentTime', 'duration', 'volume', 'isVisible', 'autoPlayDirection']
  },

  getters: {
    hasAudio: (state) => state.currentCuento && state.audioUrl,
    progress: (state) => {
      if (!isFinite(state.duration) || isNaN(state.duration) || state.duration <= 0) {
        return 0;
      }
      if (!isFinite(state.currentTime) || isNaN(state.currentTime)) {
        return 0;
      }
      return (state.currentTime / state.duration) * 100;
    },
    formattedCurrentTime: (state) => {
      if (!isFinite(state.currentTime) || isNaN(state.currentTime)) {
        return '0:00';
      }
      const minutes = Math.floor(state.currentTime / 60);
      const seconds = Math.floor(state.currentTime % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    formattedDuration: (state) => {
      if (!isFinite(state.duration) || isNaN(state.duration) || state.duration === 0) {
        return '0:00';
      }
      const minutes = Math.floor(state.duration / 60);
      const seconds = Math.floor(state.duration % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
    // Getter para verificar si un audio está prefetcheado
    isAudioPrefetched: (state) => (audioUrl) => {
      return state.prefetchedAudios.has(audioUrl);
    }
  },

  actions: {
    setAudioElement(element) {
      console.log('🎵 Configurando elemento de audio:', element ? 'disponible' : 'null');
      this.audioElement = element;
      if (element) {
        this.setupAudioListeners();
        
        // Si hay un cuento persistido, restaurar inmediatamente
        if (this.currentCuento && this.audioUrl) {
          console.log('🎵 Restaurando estado persistido en setAudioElement');
          this.restorePersistedState();
        }
      }
    },

    setupAudioListeners() {
      if (!this.audioElement) return;

      this.audioElement.addEventListener('timeupdate', () => {
        const currentTime = this.audioElement.currentTime;
        if (isFinite(currentTime) && !isNaN(currentTime) && currentTime >= 0) {
          this.currentTime = currentTime;
        } else {
          console.warn('🎵 Tiempo actual inválido:', currentTime);
          this.currentTime = 0;
        }
      });

      this.audioElement.addEventListener('loadedmetadata', () => {
        const duration = this.audioElement.duration;
        if (isFinite(duration) && !isNaN(duration) && duration > 0) {
          this.duration = duration;
          console.log('🎵 Metadata cargada, duración:', this.duration);
        } else {
          console.warn('🎵 Duración inválida:', duration);
          this.duration = 0;
        }
      });

      this.audioElement.addEventListener('loadeddata', () => {
        console.log('🎵 Datos de audio cargados');
      });

      this.audioElement.addEventListener('play', () => {
        this.isPlaying = true;
        this.isVisible = true;
        console.log('🎵 Audio iniciado');
      });

      this.audioElement.addEventListener('pause', () => {
        this.isPlaying = false;
        console.log('🎵 Audio pausado');
      });

      this.audioElement.addEventListener('ended', () => {
        this.isPlaying = false;
        this.currentTime = 0;
        console.log('🎵 Audio terminado');
        
        // Reproducción automática siempre habilitada
        this.handleAutoPlay();
      });

      this.audioElement.addEventListener('volumechange', () => {
        this.volume = this.audioElement.volume;
      });

      this.audioElement.addEventListener('error', (e) => {
        console.error('🎵 Error en audio:', e);
      });
    },

    // Nuevo método para prefetch de audio
    async prefetchAudio(audioUrl) {
      if (!audioUrl) {
        console.warn('🎵 No se puede hacer prefetch: URL de audio no proporcionada');
        return false;
      }

      // Si ya está prefetcheado, no hacer nada
      if (this.prefetchedAudios.has(audioUrl)) {
        console.log('🎵 Audio ya prefetcheado:', audioUrl);
        return true;
      }

      // Si ya se está haciendo prefetch, esperar
      if (this.isPrefetching) {
        console.log('🎵 Ya se está haciendo prefetch, esperando...');
        return false;
      }

      this.isPrefetching = true;
      console.log('🎵 Iniciando prefetch de audio:', audioUrl);

      try {
        // Crear un elemento de audio temporal para el prefetch
        const tempAudio = new Audio();
        
        // Configurar el elemento temporal
        tempAudio.preload = 'metadata';
        tempAudio.src = audioUrl;
        
        // Esperar a que se cargue la metadata
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout en prefetch'));
          }, 10000); // 10 segundos de timeout

          tempAudio.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout);
            resolve();
          }, { once: true });

          tempAudio.addEventListener('error', (error) => {
            clearTimeout(timeout);
            reject(error);
          }, { once: true });
        });

        // Marcar como prefetcheado
        this.prefetchedAudios.set(audioUrl, {
          timestamp: Date.now(),
          duration: tempAudio.duration
        });

        console.log('🎵 Prefetch completado exitosamente:', audioUrl);
        return true;

      } catch (error) {
        console.error('🎵 Error en prefetch de audio:', error);
        return false;
      } finally {
        this.isPrefetching = false;
      }
    },

    // Método para limpiar cache de audios prefetcheados
    clearPrefetchCache() {
      console.log('🎵 Limpiando cache de audios prefetcheados');
      this.prefetchedAudios.clear();
    },

    // Método para limpiar audios prefetcheados antiguos (más de 1 hora)
    cleanupOldPrefetchedAudios() {
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      let cleanedCount = 0;
      
      for (const [url, data] of this.prefetchedAudios.entries()) {
        if (data.timestamp < oneHourAgo) {
          this.prefetchedAudios.delete(url);
          cleanedCount++;
        }
      }
      
      if (cleanedCount > 0) {
        console.log(`🎵 Limpiados ${cleanedCount} audios prefetcheados antiguos`);
      }
    },

    // Método mejorado para obtener duración de audio
    async getAudioDuration(audioUrl) {
      if (!audioUrl) {
        console.warn('🎵 No se puede obtener duración: URL no proporcionada');
        return 0;
      }

      try {
        // Intentar obtener duración desde cache si está disponible
        if (this.prefetchedAudios.has(audioUrl)) {
          const cached = this.prefetchedAudios.get(audioUrl);
          if (cached.duration && cached.duration > 0) {
            console.log('🎵 Duración obtenida desde cache:', cached.duration);
            return cached.duration;
          }
        }

        // Crear elemento de audio temporal
        const tempAudio = new Audio();
        tempAudio.crossOrigin = 'anonymous'; // Intentar con CORS
        tempAudio.preload = 'metadata';

        // Promise para manejar la carga de metadatos
        const durationPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout al cargar metadatos'));
          }, 15000); // 15 segundos de timeout

          tempAudio.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout);
            const duration = tempAudio.duration;
            if (isFinite(duration) && !isNaN(duration) && duration > 0) {
              resolve(duration);
            } else {
              reject(new Error('Duración inválida obtenida'));
            }
          }, { once: true });

          tempAudio.addEventListener('error', (error) => {
            clearTimeout(timeout);
            reject(new Error(`Error al cargar audio: ${error.message}`));
          }, { once: true });

          // Intentar cargar el audio
          tempAudio.src = audioUrl;
        });

        const duration = await durationPromise;
        console.log('🎵 Duración obtenida exitosamente:', duration);
        
        // Guardar en cache
        this.prefetchedAudios.set(audioUrl, {
          timestamp: Date.now(),
          duration: duration
        });

        return duration;

      } catch (error) {
        console.error('🎵 Error al obtener duración:', error.message);
        
        // Fallback: intentar con fetch para verificar si el archivo existe
        try {
          const response = await fetch(audioUrl, { method: 'HEAD' });
          if (!response.ok) {
            console.error('🎵 Archivo de audio no encontrado o no accesible');
          }
        } catch (fetchError) {
          console.error('🎵 Error al verificar archivo:', fetchError.message);
        }
        
        return 0;
      }
    },

    // Método específico para archivos WAV problemáticos
    async getWavDuration(audioUrl) {
      if (!audioUrl) return 0;

      try {
        // Método 1: Intentar con fetch y analizar el header WAV
        const response = await fetch(audioUrl, { 
          method: 'GET',
          headers: {
            'Range': 'bytes=0-43' // Solo los primeros 44 bytes del header WAV
          }
        });

        if (response.ok) {
          const arrayBuffer = await response.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          
          // Verificar que es un archivo WAV válido (RIFF header)
          if (uint8Array.length >= 44 && 
              String.fromCharCode(...uint8Array.slice(0, 4)) === 'RIFF' &&
              String.fromCharCode(...uint8Array.slice(8, 12)) === 'WAVE') {
            
            // Obtener el tamaño del archivo desde el header
            const fileSize = new DataView(arrayBuffer).getUint32(4, true);
            
            // Intentar obtener información del chunk de datos
            let dataSize = 0;
            let offset = 12;
            
            while (offset < uint8Array.length - 8) {
              const chunkId = String.fromCharCode(...uint8Array.slice(offset, offset + 4));
              const chunkSize = new DataView(arrayBuffer).getUint32(offset + 4, true);
              
              if (chunkId === 'data') {
                dataSize = chunkSize;
                break;
              }
              
              offset += 8 + chunkSize;
            }
            
            // Si encontramos el chunk de datos, intentar calcular la duración
            if (dataSize > 0) {
              // Necesitamos obtener información del formato
              const formatResponse = await fetch(audioUrl, {
                method: 'GET',
                headers: {
                  'Range': 'bytes=20-36' // Chunk de formato
                }
              });
              
              if (formatResponse.ok) {
                const formatBuffer = await formatResponse.arrayBuffer();
                const formatView = new DataView(formatBuffer);
                
                // Obtener información del formato
                const audioFormat = formatView.getUint16(0, true);
                const numChannels = formatView.getUint16(2, true);
                const sampleRate = formatView.getUint32(4, true);
                const byteRate = formatView.getUint32(8, true);
                const blockAlign = formatView.getUint16(12, true);
                const bitsPerSample = formatView.getUint16(14, true);
                
                console.log('🎵 Información WAV:', {
                  audioFormat,
                  numChannels,
                  sampleRate,
                  byteRate,
                  blockAlign,
                  bitsPerSample,
                  dataSize
                });
                
                // Calcular duración
                if (sampleRate > 0 && numChannels > 0 && bitsPerSample > 0) {
                  const bytesPerSample = bitsPerSample / 8;
                  const bytesPerSecond = sampleRate * numChannels * bytesPerSample;
                  const duration = dataSize / bytesPerSecond;
                  
                  if (isFinite(duration) && duration > 0) {
                    console.log('🎵 Duración calculada desde header WAV:', duration);
                    return duration;
                  }
                }
              }
            }
          }
        }

        // Método 2: Intentar con un elemento de audio con preload completo
        console.log('🎵 Intentando método alternativo para WAV...');
        const tempAudio = new Audio();
        tempAudio.crossOrigin = 'anonymous';
        tempAudio.preload = 'auto'; // Cambiar a auto para cargar todo el archivo
        
        const durationPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout al cargar WAV completo'));
          }, 30000); // 30 segundos para archivos grandes

          tempAudio.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout);
            const duration = tempAudio.duration;
            if (isFinite(duration) && !isNaN(duration) && duration > 0) {
              resolve(duration);
            } else {
              reject(new Error('Duración inválida en WAV'));
            }
          }, { once: true });

          tempAudio.addEventListener('canplaythrough', () => {
            clearTimeout(timeout);
            const duration = tempAudio.duration;
            if (isFinite(duration) && !isNaN(duration) && duration > 0) {
              resolve(duration);
            } else {
              reject(new Error('Duración inválida en WAV (canplaythrough)'));
            }
          }, { once: true });

          tempAudio.addEventListener('error', (error) => {
            clearTimeout(timeout);
            reject(new Error(`Error al cargar WAV: ${error.message}`));
          }, { once: true });

          tempAudio.src = audioUrl;
        });

        const duration = await durationPromise;
        console.log('🎵 Duración obtenida con método alternativo:', duration);
        return duration;

      } catch (error) {
        console.error('🎵 Error en getWavDuration:', error.message);
        
        // Método 3: Fallback - estimar basado en el tamaño del archivo
        try {
          const response = await fetch(audioUrl, { method: 'HEAD' });
          const contentLength = response.headers.get('content-length');
          
          if (contentLength) {
            const fileSizeBytes = parseInt(contentLength);
            // Estimación aproximada: 1.23 MB ≈ 10-15 minutos de audio WAV
            // Esta es una estimación muy aproximada
            const estimatedDuration = (fileSizeBytes / (44100 * 2 * 2)) / 60; // minutos
            console.log('🎵 Duración estimada basada en tamaño:', estimatedDuration, 'minutos');
            return estimatedDuration * 60; // convertir a segundos
          }
        } catch (fallbackError) {
          console.error('🎵 Error en fallback:', fallbackError.message);
        }
        
        return 0;
      }
    },

    // Método para verificar si un archivo de audio es accesible
    async checkAudioAccessibility(audioUrl) {
      if (!audioUrl) return false;

      try {
        const response = await fetch(audioUrl, { 
          method: 'HEAD',
          mode: 'cors'
        });
        
        const isAccessible = response.ok;
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        
        console.log('🎵 Verificación de accesibilidad:', {
          url: audioUrl,
          accessible: isAccessible,
          contentType,
          contentLength
        });
        
        return isAccessible;
      } catch (error) {
        console.error('🎵 Error al verificar accesibilidad:', error.message);
        return false;
      }
    },

    playCuento(cuento) {
      console.log('🎵 Reproduciendo cuento:', cuento);
      
      // Verificar si el cuento tiene audio disponible
      const audioUrl = cuento.audio_v2 || cuento.audio;
      if (!audioUrl) {
        console.warn('🎵 El cuento no tiene audio disponible:', cuento);
        this.isLoading = false;
        return;
      }
      
      // Evitar múltiples cargas simultáneas
      if (this.isLoading) {
        console.log('🎵 Ya hay una carga en progreso, cancelando...');
        return;
      }
      
      this.isLoading = true;
      
      // Verificar si el audio está prefetcheado
      const isPrefetched = this.prefetchedAudios.has(audioUrl);
      console.log('🎵 Estado del audio:', { isPrefetched, audioUrl });
      
      // Pausar y limpiar el audio actual
      if (this.audioElement) {
        this.audioElement.pause();
        this.audioElement.src = '';
      }
      
      // Actualizar el estado
      this.currentCuento = cuento;
      this.audioUrl = audioUrl;
      this.isVisible = true;
      this.currentTime = 0;
      this.duration = 0;
      this.isPlaying = false;
      
      // Si hay un elemento de audio, configurar y reproducir
      if (this.audioElement) {
        // Configurar la nueva fuente
        this.audioElement.src = cuento.audio;
        
        // Función para reproducir cuando esté listo
        const playWhenReady = () => {
          console.log('🎵 Audio listo, iniciando reproducción');
          this.isLoading = false;
          
          // Intentar reproducir inmediatamente
          this.audioElement.play().catch(error => {
            if (error.name !== 'AbortError') {
              console.error('Error reproduciendo audio:', error);
              // Si falla, intentar de nuevo después de un delay
              setTimeout(() => {
                console.log('🎵 Reintentando reproducción después de delay');
                this.audioElement.play().catch(retryError => {
                  if (retryError.name !== 'AbortError') {
                    console.error('Error en reintento de reproducción:', retryError);
                  }
                  this.isLoading = false;
                });
              }, 1000);
            }
            this.isLoading = false;
          });
        };
        
        // Si está prefetcheado o ya está cargado, reproducir inmediatamente
        if (isPrefetched || this.audioElement.readyState >= 2) {
          console.log('🎵 Audio prefetcheado o listo, reproduciendo inmediatamente');
          playWhenReady();
        } else {
          // Esperar a que se cargue
          this.audioElement.addEventListener('canplay', playWhenReady, { once: true });
          
          // Timeout de seguridad más agresivo
          setTimeout(() => {
            if (this.isLoading) {
              console.log('🎵 Timeout de carga, intentando reproducir de todas formas');
              this.isLoading = false;
              playWhenReady();
            }
          }, 2000);
        }
      } else {
        this.isLoading = false;
      }
    },

    play() {
      console.log('🎵 Intentando reproducir audio');
      
      // Verificar y configurar el elemento de audio si es necesario
      if (!this.ensureAudioElementConfigured()) {
        console.warn('🎵 No se puede reproducir: elemento de audio no configurado correctamente');
        return;
      }
      
      this.audioElement.play().catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Error reproduciendo audio:', error);
          // Si falla, intentar de nuevo después de un delay
          setTimeout(() => {
            console.log('🎵 Reintentando reproducción después de error');
            this.audioElement.play().catch(retryError => {
              if (retryError.name !== 'AbortError') {
                console.error('Error en reintento de reproducción:', retryError);
              }
            });
          }, 1000);
        }
      });
    },

    pause() {
      if (this.audioElement) {
        this.audioElement.pause();
      }
    },

    togglePlay() {
      console.log('🎵 Toggle play - Estado actual:', this.isPlaying);
      
      if (this.isPlaying) {
        this.pause();
      } else {
        // Verificar y configurar el elemento de audio si es necesario
        if (!this.ensureAudioElementConfigured()) {
          console.warn('🎵 No se puede reproducir: elemento de audio no configurado correctamente');
          return;
        }
        
        this.play();
      }
    },

    seekTo(time) {
      if (this.audioElement) {
        this.audioElement.currentTime = time;
      }
    },

    setVolume(volume) {
      if (this.audioElement) {
        this.audioElement.volume = volume;
      }
    },

    closeAudio() {
      console.log('🔇 Cerrando reproductor de audio');
      this.pause();
      this.currentCuento = null;
      this.audioUrl = null;
      this.currentTime = 0;
      this.duration = 0;
      // No ocultamos el reproductor, solo limpiamos el audio
      
      if (this.audioElement) {
        this.audioElement.src = '';
      }
    },

    hidePlayer() {
      this.isVisible = false;
    },

    showPlayer() {
      this.isVisible = true;
    },

    togglePlayerVisibility() {
      this.isVisible = !this.isVisible;
    },

    // Método para restaurar el estado persistido
    restorePersistedState() {
      if (this.currentCuento && this.audioElement) {
        console.log('🎵 Restaurando estado persistido');
        
        // Verificar si hay URL de audio disponible
        const audioUrl = this.currentCuento.audio_v2 || this.currentCuento.audio;
        if (!audioUrl) {
          console.warn('🎵 No se puede restaurar estado: no hay URL de audio disponible');
          return;
        }
        
        // Actualizar la URL de audio si es necesario
        if (this.audioUrl !== audioUrl) {
          this.audioUrl = audioUrl;
        }
        
        // Configurar la fuente de audio
        if (this.audioElement.src !== this.audioUrl) {
          this.audioElement.src = this.audioUrl;
        }
        
        // Restaurar el tiempo de reproducción
        if (this.currentTime > 0) {
          this.audioElement.currentTime = this.currentTime;
        }
        
        // Restaurar el volumen
        this.audioElement.volume = this.volume;
      } else {
        console.warn('🎵 No se puede restaurar estado: cuento o elemento de audio faltante');
        console.log('🎵 Estado actual:', {
          currentCuento: !!this.currentCuento,
          audioElement: !!this.audioElement
        });
      }
    },

    // Método para verificar y restaurar el estado si es necesario
    ensureAudioElementConfigured() {
      if (!this.audioElement) {
        console.warn('🎵 No hay elemento de audio configurado');
        return false;
      }
      
      if (!this.currentCuento) {
        console.warn('🎵 No hay cuento configurado');
        return false;
      }
      
      // Verificar si hay URL de audio disponible
      const audioUrl = this.currentCuento.audio_v2 || this.currentCuento.audio;
      if (!audioUrl) {
        console.warn('🎵 No hay URL de audio disponible en el cuento');
        return false;
      }
      
      // Actualizar la URL de audio si es necesario
      if (this.audioUrl !== audioUrl) {
        this.audioUrl = audioUrl;
      }
      
      // Verificar si la fuente está configurada correctamente
      if (this.audioElement.src !== this.audioUrl) {
        console.log('🎵 Configurando fuente de audio:', this.audioUrl);
        this.audioElement.src = this.audioUrl;
      }
      
      return true;
    },

    // Método para manejar la reproducción automática
    async handleAutoPlay() {
      console.log('🎵 Iniciando reproducción automática');
      
      // Emitir evento para que el componente maneje la navegación
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('audioAutoPlay', {
          detail: {
            direction: this.autoPlayDirection,
            currentCuento: this.currentCuento
          }
        });
        window.dispatchEvent(event);
      }
    },

    // Método para cambiar la dirección de reproducción automática
    setAutoPlayDirection(direction) {
      if (direction === 'next' || direction === 'previous') {
        this.autoPlayDirection = direction;
        console.log('🎵 Dirección de reproducción automática cambiada a:', direction);
      }
    }
  }
}); 